export type FilterKeys<T extends Record<string, unknown>, Filter extends string> = Extract<
    keyof T,
    Filter
>;
// an HTMLElement that has been attached to the DOM (all attached HTMLElements have an offsetParent)
export type DOMElement<T extends HTMLElement = HTMLElement> = T & { offsetParent: HTMLElement };
export type DOMEvent<T extends Event> = T & { target: DOMElement };

function isObject(arg: unknown): arg is Record<PropertyKey, unknown> {
    const type = typeof arg;
    return type === 'function' || type === 'object';
}

function duckTypeHTMLElement(element: unknown): element is HTMLElement {
    // dataset is just an arbitrary property on HTMLElement
    return !!element && isObject(element) && !!element?.dataset;
}
export function isDOMElement<T extends HTMLElement>(arg: T | null): arg is DOMElement<T> {
    return duckTypeHTMLElement(arg) && duckTypeHTMLElement(arg.offsetParent);
}

export function isDOMEvent<T extends Event>(event: T): event is DOMEvent<T> {
    if (duckTypeHTMLElement(event.target)) {
        return true;
    }
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error(
            `This event target must be an HTMLElement. Got ${event.target?.constructor.name}`
        );
    }
    return false;
}

export function filterUndefined<T extends Record<string, unknown>, K extends readonly (keyof T)[]>(
    obj: T,
    keys: K
): Partial<T> {
    const filtered: Partial<T> = {};
    for (const key of keys) {
        const val = obj[key];
        if (val) filtered[key] = val;
    }
    return filtered;
}
